import { useState, useRef, useCallback } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import {
  Receipt,
  Plus,
  PencilSimple,
  TrashSimple,
  Star,
  Bell,
  CircleNotch,
  Warning,
  Palette,
  UploadSimple,
  X as XIcon,
  CheckCircle,
  Image as ImageIcon,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { PageHeader } from '@/components/shared/PageHeader';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  TEMPLATE_KIND_LABELS,
  fetchBillTemplates,
  deleteBillTemplate,
  fetchReminderConfig,
  updateReminderConfig,
  type BillTemplate,
  type BillTemplateKind,
} from '@/lib/billSettingsApi';
import { fetchChambersPrefs, updateChambersPrefs, type ChambersPrefs } from '@/lib/meApi';
import { fetchUsers } from '@/lib/usersApi';
import { BillTemplateModal } from '@/components/bill-settings/BillTemplateModal';
import { useAuthStore } from '@/features/auth/store';
import { useChambersMode } from '@/lib/useChambersMode';
import { getApiErrorMessage } from '@/lib/api';

type Tab = 'templates' | 'reminders' | 'appearance';

const KIND_ORDER: BillTemplateKind[] = ['bill', 'trust', 'outstanding', 'reminder'];

export default function BillSettingsPage() {
  const navigate = useNavigate();
  const qc = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const isAdmin = user?.memberType === 'admin';
  const myBarristerId = user?.barristerId ?? null;

  // Solo practices have no chambers-shared context, so the "Chambers shared"
  // scope must not appear (a solo barrister IS the practice). Reuse the central
  // deployment-mode signal rather than inventing a local check.
  const { isSolo } = useChambersMode();

  // Scope (per docs/14): per-owner Bill Settings. Barristers default to
  // "My settings"; chambers admins default to "Chambers shared".
  type Scope = 'mine' | 'chambers';
  const [scope, setScope] = useState<Scope>(myBarristerId ? 'mine' : 'chambers');

  const [tab, setTab] = useState<Tab>('templates');
  const [createKind, setCreateKind] = useState<BillTemplateKind | null>(null);
  const [editTarget, setEditTarget] = useState<BillTemplate | null>(null);

  // Concrete owner for the current scope — used by mutations + reminder
  // config fetch.
  const owner = scope === 'mine' && myBarristerId
    ? { ownerType: 'barrister' as const, barristerOwnerId: myBarristerId }
    : { ownerType: 'chambers' as const, barristerOwnerId: null as string | null };

  // ─── Templates ───────────────────────────────────────────────
  const templatesQ = useQuery({
    queryKey: ['bill-templates', { scope }],
    queryFn: () => fetchBillTemplates({ scope }),
  });

  const deleteMut = useMutation({
    mutationFn: (id: string) => deleteBillTemplate(id),
    onSuccess: () => {
      toast.success('Template deleted');
      qc.invalidateQueries({ queryKey: ['bill-templates'] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not delete template')),
  });

  const groupedTemplates = (templatesQ.data ?? []).reduce<Record<BillTemplateKind, BillTemplate[]>>(
    (acc, t) => {
      if (!acc[t.kind]) acc[t.kind] = [];
      acc[t.kind]!.push(t);
      return acc;
    },
    {} as Record<BillTemplateKind, BillTemplate[]>,
  );

  return (
    <div className="space-y-5">
      <PageHeader
        title="Bill settings"
        subtitle="Customise the language of invoices and chasers, and the schedule for automated reminders."
        onBack={() => navigate(-1)}
      />

      {/* Scope tabs — only when a chambers context actually exists. A solo
          barrister has no "Chambers shared" scope, so hide the toggle entirely
          and keep them on their own settings. */}
      {myBarristerId && !isSolo && (
        <div className="flex items-center gap-1 border-b border-border">
          <button
            onClick={() => setScope('mine')}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
              scope === 'mine'
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            My settings
          </button>
          <button
            onClick={() => setScope('chambers')}
            className={`-mb-px border-b-2 px-3 py-2 text-sm font-medium transition ${
              scope === 'chambers'
                ? 'border-brand-600 text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground'
            }`}
          >
            Chambers shared
          </button>
        </div>
      )}

      {/* Tabs — scrollable at phone width. These three were wider than a 390px screen and
          "Invoice appearance" sat past the right edge with no way to reach it. */}
      <div className="no-scrollbar -mb-px flex items-center gap-1 overflow-x-auto overflow-y-hidden border-b border-border">
        <button
          onClick={() => setTab('templates')}
          className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm font-medium transition ${
            tab === 'templates'
              ? 'border-b-2 border-brand-600 text-brand-700 dark:text-brand-300'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Receipt size={14} weight={tab === 'templates' ? 'fill' : 'duotone'} />
          Templates
          {templatesQ.data && (
            <span className="ml-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold">
              {templatesQ.data.length}
            </span>
          )}
        </button>
        <button
          onClick={() => setTab('reminders')}
          className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm font-medium transition ${
            tab === 'reminders'
              ? 'border-b-2 border-brand-600 text-brand-700 dark:text-brand-300'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Bell size={14} weight={tab === 'reminders' ? 'fill' : 'duotone'} />
          Automated reminders
        </button>
        <button
          onClick={() => setTab('appearance')}
          className={`flex shrink-0 items-center gap-1.5 px-3 py-2 text-sm font-medium transition ${
            tab === 'appearance'
              ? 'border-b-2 border-brand-600 text-brand-700 dark:text-brand-300'
              : 'text-muted-foreground hover:text-foreground'
          }`}
        >
          <Palette size={14} weight={tab === 'appearance' ? 'fill' : 'duotone'} />
          Invoice appearance
        </button>
      </div>

      {tab === 'templates' && (
        <TemplatesTab
          isAdmin={isAdmin}
          scope={scope}
          owner={owner}
          canEditScope={scope === 'mine' || isAdmin}
          loading={templatesQ.isLoading}
          error={templatesQ.error}
          grouped={groupedTemplates}
          totalCount={templatesQ.data?.length ?? 0}
          onCreate={(kind) => setCreateKind(kind)}
          onEdit={(t) => setEditTarget(t)}
          onDelete={(t) => {
            if (window.confirm(`Delete "${t.name}"? This cannot be undone.`)) {
              deleteMut.mutate(t.id);
            }
          }}
        />
      )}

      {tab === 'reminders' && (
        <RemindersTab
          owner={owner}
          canEdit={scope === 'mine' || isAdmin}
          templates={templatesQ.data ?? []}
        />
      )}

      {tab === 'appearance' && (
        <InvoiceAppearanceTab />
      )}

      {createKind && (
        <BillTemplateModal
          defaultKind={createKind}
          forceOwnerType={scope === 'mine' ? 'barrister' : 'chambers'}
          forceBarristerOwnerId={scope === 'mine' ? myBarristerId : null}
          onClose={() => setCreateKind(null)}
          onSaved={() => setCreateKind(null)}
        />
      )}
      {editTarget && (
        <BillTemplateModal
          initial={editTarget}
          onClose={() => setEditTarget(null)}
          onSaved={() => setEditTarget(null)}
        />
      )}
    </div>
  );
}

// ─── Templates tab ──────────────────────────────────────────

function TemplatesTab(props: {
  isAdmin: boolean;
  scope: 'mine' | 'chambers';
  owner: { ownerType: 'chambers' | 'barrister'; barristerOwnerId: string | null };
  canEditScope: boolean;
  loading: boolean;
  error: unknown;
  grouped: Record<BillTemplateKind, BillTemplate[]>;
  totalCount: number;
  onCreate: (kind: BillTemplateKind) => void;
  onEdit: (t: BillTemplate) => void;
  onDelete: (t: BillTemplate) => void;
}) {
  const { isAdmin, canEditScope, loading, error, grouped, totalCount, onCreate, onEdit, onDelete } = props;
  void isAdmin; // retained for prop compatibility; per-row write gates use canEditScope
  const cap = 30;
  const remaining = cap - totalCount;
  const atLimit = totalCount >= cap;

  if (loading) {
    return (
      <Card className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
        <CircleNotch size={14} className="animate-spin" /> Loading templates…
      </Card>
    );
  }

  if (error) {
    return (
      <Card className="px-5 py-6 text-sm text-red-700 dark:text-red-300">
        {getApiErrorMessage(error, 'Could not load templates.')}
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Cap notice */}
      <div className="flex items-center justify-between rounded-md border border-border bg-muted/30 px-3 py-2 text-xs">
        <span className="text-muted-foreground">
          {totalCount} of {cap} templates · {remaining > 0 ? `${remaining} remaining` : 'limit reached'}
        </span>
        {atLimit && (
          <span className="flex items-center gap-1 text-amber-700 dark:text-amber-300">
            <Warning size={11} weight="fill" /> Delete or deactivate one before creating another
          </span>
        )}
      </div>

      {totalCount === 0 && (
        <Card className="px-5 py-12 text-center">
          <Receipt className="mx-auto mb-3 h-9 w-9 text-muted-foreground" weight="duotone" />
          <div className="mb-1 text-sm font-medium">No templates yet</div>
          <p className="mx-auto max-w-md text-xs text-muted-foreground">
            Templates control the language on invoices, trust requests, and chase letters.
            Create one for each scenario the chambers wants to control.
          </p>
        </Card>
      )}

      {/* Grouped sections */}
      {KIND_ORDER.map((kind) => {
        const list = grouped[kind] ?? [];
        if (list.length === 0 && totalCount > 0) {
          // Show empty section as a stub when other kinds have data — gives
          // admins a one-click create button for the missing kind.
          return (
            <Card key={kind} className="px-5 py-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                    {TEMPLATE_KIND_LABELS[kind]}
                  </div>
                  <p className="mt-0.5 text-xs text-muted-foreground">No template defined yet.</p>
                </div>
                {canEditScope && !atLimit && (
                  <Button size="sm" variant="outline" onClick={() => onCreate(kind)} leftIcon={<Plus className="h-3 w-3" />}>
                    Create
                  </Button>
                )}
              </div>
            </Card>
          );
        }
        if (list.length === 0) return null;
        return (
          <Card key={kind} className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-border bg-muted/30 px-5 py-2.5">
              <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
                {TEMPLATE_KIND_LABELS[kind]} · {list.length}
              </div>
              {canEditScope && !atLimit && (
                <Button size="sm" variant="outline" onClick={() => onCreate(kind)} leftIcon={<Plus className="h-3 w-3" />}>
                  New
                </Button>
              )}
            </div>
            <ul className="divide-y divide-border">
              {list.map((t) => (
                <li key={t.id} className={`flex items-start gap-3 px-5 py-3 ${!t.isActive ? 'opacity-60' : ''}`}>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="truncate font-medium">{t.name}</span>
                      {t.isDefault && (
                        <span title="Default for this kind">
                          <Star size={11} weight="fill" className="text-amber-500" />
                        </span>
                      )}
                      {!t.isActive && (
                        <Badge variant="outline" className="h-4 px-1 text-[9px]">Inactive</Badge>
                      )}
                    </div>
                    <div className="mt-0.5 truncate text-xs text-muted-foreground">
                      <span className="">{t.subject}</span>
                    </div>
                  </div>
                  {canEditScope && (
                    <div className="flex shrink-0 items-center gap-1">
                      <button
                        onClick={() => onEdit(t)}
                        title="Edit"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                      >
                        <PencilSimple size={13} />
                      </button>
                      <button
                        onClick={() => onDelete(t)}
                        title="Delete"
                        className="flex h-7 w-7 items-center justify-center rounded-md text-muted-foreground hover:bg-red-50 hover:text-red-600 dark:hover:text-red-400"
                      >
                        <TrashSimple size={13} />
                      </button>
                    </div>
                  )}
                </li>
              ))}
            </ul>
          </Card>
        );
      })}

      {/* Add-first-template buttons when entirely empty */}
      {totalCount === 0 && canEditScope && (
        <Card className="px-5 py-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
            Create your first template
          </div>
          <div className="flex flex-wrap gap-2">
            {KIND_ORDER.map((kind) => (
              <Button
                key={kind}
                size="sm"
                variant="outline"
                onClick={() => onCreate(kind)}
                leftIcon={<Plus className="h-3 w-3" />}
              >
                {TEMPLATE_KIND_LABELS[kind]}
              </Button>
            ))}
          </div>
        </Card>
      )}

      {!canEditScope && totalCount > 0 && (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Read-only view — only the owner or chambers admin can edit templates in this scope.
        </div>
      )}
    </div>
  );
}

// ─── Reminders tab ──────────────────────────────────────────

function RemindersTab(props: {
  owner: { ownerType: 'chambers' | 'barrister'; barristerOwnerId: string | null };
  canEdit: boolean;
  templates: BillTemplate[];
}) {
  const { owner, canEdit, templates } = props;
  const qc = useQueryClient();

  // Stable cache key per owner so switching scope refetches the right config.
  const ownerKey = `${owner.ownerType}:${owner.barristerOwnerId ?? ''}`;

  const configQ = useQuery({
    queryKey: ['bill-reminder-config', ownerKey],
    queryFn: () => fetchReminderConfig(owner),
  });

  const usersQ = useQuery({
    queryKey: ['users', 'firm-users-active'],
    queryFn: () => fetchUsers({ status: 'active', limit: 100 }),
    enabled: canEdit,
  });

  // Local form state mirrors the server config; reset when server data
  // lands OR when owner changes (track loadedFor to detect scope switch).
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [enabled, setEnabled] = useState<boolean | null>(null);
  const [firstReminderDays, setFirstReminderDays] = useState<number | ''>('');
  const [repeatEveryDays, setRepeatEveryDays] = useState<number | ''>('');
  const [pauseOnPaymentPlan, setPauseOnPaymentPlan] = useState<boolean | null>(null);
  const [sendCombinedEmail, setSendCombinedEmail] = useState<boolean | null>(null);
  const [repliesToUserId, setRepliesToUserId] = useState<string | null>(null);
  const [templateId, setTemplateId] = useState<string | null>(null);

  // Hydrate when server config arrives or when owner switches.
  if (configQ.data && loadedFor !== ownerKey) {
    setLoadedFor(ownerKey);
    setEnabled(configQ.data.enabled);
    setFirstReminderDays(configQ.data.firstReminderDays);
    setRepeatEveryDays(configQ.data.repeatEveryDays);
    setPauseOnPaymentPlan(configQ.data.pauseOnPaymentPlan);
    setSendCombinedEmail(configQ.data.sendCombinedEmail);
    setRepliesToUserId(configQ.data.repliesToUserId);
    setTemplateId(configQ.data.templateId);
  }

  const saveMut = useMutation({
    mutationFn: () =>
      updateReminderConfig({
        ownerType: owner.ownerType,
        barristerOwnerId: owner.barristerOwnerId,
        enabled: enabled ?? false,
        firstReminderDays: typeof firstReminderDays === 'number' ? firstReminderDays : undefined,
        repeatEveryDays: typeof repeatEveryDays === 'number' ? repeatEveryDays : undefined,
        pauseOnPaymentPlan: pauseOnPaymentPlan ?? true,
        sendCombinedEmail: sendCombinedEmail ?? true,
        repliesToUserId,
        templateId,
      }),
    onSuccess: () => {
      toast.success('Reminder settings saved');
      qc.invalidateQueries({ queryKey: ['bill-reminder-config', ownerKey] });
    },
    onError: (err) => toast.error(getApiErrorMessage(err, 'Could not save reminder settings')),
  });

  const reminderTemplates = templates.filter((t) => t.kind === 'reminder' && t.isActive);

  if (configQ.isLoading || enabled === null) {
    return (
      <Card className="flex items-center gap-2 px-5 py-6 text-sm text-muted-foreground">
        <CircleNotch size={14} className="animate-spin" /> Loading reminder settings…
      </Card>
    );
  }

  return (
    <div className="space-y-3">
      <Card className="px-5 py-4">
        <label className="flex cursor-pointer items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            disabled={!canEdit || saveMut.isPending}
            className="h-4 w-4 cursor-pointer accent-brand-600"
          />
          <span className="font-medium">Enable automated reminders</span>
        </label>
        <p className="mt-1 text-xs text-muted-foreground">
          When on, the system will automatically email reminders for unpaid invoices on the schedule below.
          The cron job that actually sends them ships in a follow-up update — for now this stores your policy.
        </p>
      </Card>

      <Card className="space-y-3 px-5 py-4">
        <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          Schedule
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              First reminder — days after issue
            </label>
            <input
              type="number"
              min={0}
              max={365}
              value={firstReminderDays}
              onChange={(e) => setFirstReminderDays(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={!canEdit || !enabled || saveMut.isPending}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Repeat — every N days until paid
            </label>
            <input
              type="number"
              min={1}
              max={365}
              value={repeatEveryDays}
              onChange={(e) => setRepeatEveryDays(e.target.value === '' ? '' : Number(e.target.value))}
              disabled={!canEdit || !enabled || saveMut.isPending}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            />
          </div>
        </div>

        <label className="flex cursor-pointer items-center gap-2 text-[12.5px]">
          <input
            type="checkbox"
            checked={pauseOnPaymentPlan ?? false}
            onChange={(e) => setPauseOnPaymentPlan(e.target.checked)}
            disabled={!canEdit || !enabled || saveMut.isPending}
            className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
          />
          Pause when client is on an active payment plan
        </label>

        <label className="flex cursor-pointer items-center gap-2 text-[12.5px]">
          <input
            type="checkbox"
            checked={sendCombinedEmail ?? false}
            onChange={(e) => setSendCombinedEmail(e.target.checked)}
            disabled={!canEdit || !enabled || saveMut.isPending}
            className="h-3.5 w-3.5 cursor-pointer accent-brand-600"
          />
          Bundle multiple overdue invoices into one email per recipient
        </label>
      </Card>

      <Card className="space-y-3 px-5 py-4">
        <div className="text-xs font-semibold uppercase tracking-[0.06em] text-muted-foreground">
          Email content
        </div>
        <div className="grid gap-3 md:grid-cols-2">
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Replies go to (firm user)
            </label>
            <select
              value={repliesToUserId ?? ''}
              onChange={(e) => setRepliesToUserId(e.target.value || null)}
              disabled={!canEdit || !enabled || saveMut.isPending}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="">— Default (chambers email) —</option>
              {usersQ.data?.map((u) => (
                <option key={u.id} value={u.id}>
                  {u.firstName} {u.lastName} · {u.email}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-muted-foreground">
              Template
            </label>
            <select
              value={templateId ?? ''}
              onChange={(e) => setTemplateId(e.target.value || null)}
              disabled={!canEdit || !enabled || saveMut.isPending}
              className="w-full rounded-md border border-input bg-background px-2 py-1.5 text-sm"
            >
              <option value="">— No template (use default copy) —</option>
              {reminderTemplates.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.name}
                  {t.isDefault ? ' (default)' : ''}
                </option>
              ))}
            </select>
            {reminderTemplates.length === 0 && (
              <p className="mt-1 text-xs text-muted-foreground">
                No reminder templates available — create one on the Templates tab first.
              </p>
            )}
          </div>
        </div>
      </Card>

      {canEdit ? (
        <div className="flex justify-end">
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="gap-1.5">
            {saveMut.isPending && <CircleNotch size={14} className="animate-spin" />}
            Save reminder settings
          </Button>
        </div>
      ) : (
        <div className="rounded-md border border-border bg-muted/30 px-3 py-2 text-xs text-muted-foreground">
          Read-only view — only the owner or chambers admin can change reminder settings in this scope.
        </div>
      )}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════
// INVOICE APPEARANCE — Premium designer experience
// ═══════════════════════════════════════════════════════════════

type InvoiceHeaderMode = 'text' | 'logo' | 'full_image';

// ─── Live invoice preview ─────────────────────────────────────

interface PreviewProps {
  headerMode: InvoiceHeaderMode;
  logoUrl: string;
  fullHeaderUrl: string;
  headerCustomText: string;
  footerText: string;
  prefs: ChambersPrefs | undefined;
}

function InvoicePreview({ headerMode, logoUrl, fullHeaderUrl, headerCustomText, footerText, prefs }: PreviewProps) {
  const firmName = prefs?.name ?? 'Your Chambers';
  const city = prefs?.city ?? 'London';
  const postcode = prefs?.postcode ?? '';
  const email = prefs?.email ?? '';
  const phone = prefs?.phone ?? '';
  const vat = prefs?.vatNumber ?? '';
  const addr1 = prefs?.addressLine1 ?? '1 Temple Gardens';
  const addr2 = prefs?.addressLine2;

  return (
    <div className="overflow-hidden rounded-lg border border-border bg-white shadow-lg">
      <div className="border-b border-border bg-muted/30 px-4 py-2 flex items-center justify-between">
        <span className="text-[11px] font-medium text-muted-foreground tracking-wide uppercase">Preview</span>
        <span className="text-[10px] text-muted-foreground">Updates live as you configure</span>
      </div>

      <div className="bg-white p-5" style={{ fontSize: '9px', lineHeight: '1.4', fontFamily: 'Georgia, serif' }}>

        {headerMode === 'full_image' && fullHeaderUrl ? (
          <div className="mb-3">
            <img src={fullHeaderUrl} alt="Header" className="w-full rounded" style={{ maxHeight: '80px', objectFit: 'cover' }}
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
            <div style={{ textAlign: 'center', marginTop: '6px' }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#2563eb', letterSpacing: '0.05em' }}>INVOICE</div>
              <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>FN-2026-001</div>
              <div style={{ color: '#64748b', marginTop: '4px' }}>Issue date: 04 Jun 2026</div>
              <div style={{ color: '#64748b' }}>Due date: 04 Jul 2026</div>
              <div style={{ color: '#64748b', marginTop: '2px', fontSize: '8px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>DRAFT</div>
            </div>
          </div>
        ) : (
          <div className="flex justify-between items-start mb-3">
            <div style={{ maxWidth: '55%' }}>
              {headerMode === 'logo' && logoUrl && (
                <img src={logoUrl} alt="Logo" className="mb-1.5 rounded"
                  style={{ maxHeight: '36px', maxWidth: '120px', objectFit: 'contain' }}
                  onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
              )}
              <div style={{ fontSize: '11px', fontWeight: 700, color: '#0f172a' }}>{firmName}</div>
              {headerCustomText && headerMode === 'text' && (
                <div style={{ color: '#334155', marginTop: '1px', whiteSpace: 'pre-line' }}>{headerCustomText}</div>
              )}
              <div style={{ color: '#64748b', marginTop: '2px' }}>{addr1}{addr2 ? ', ' + addr2 : ''}</div>
              <div style={{ color: '#64748b' }}>{[city, postcode].filter(Boolean).join(' ')}</div>
              {email && <div style={{ color: '#64748b' }}>{email}</div>}
              {phone && <div style={{ color: '#64748b' }}>{phone}</div>}
              {vat && <div style={{ color: '#64748b' }}>VAT no. {vat}</div>}
            </div>
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: '16px', fontWeight: 700, color: '#2563eb', letterSpacing: '0.05em' }}>INVOICE</div>
              <div style={{ fontWeight: 600, color: '#0f172a', marginTop: '2px' }}>FN-2026-001</div>
              <div style={{ color: '#64748b', marginTop: '4px' }}>Issue date: 04 Jun 2026</div>
              <div style={{ color: '#64748b' }}>Due date: 04 Jul 2026</div>
              <div style={{ color: '#64748b', marginTop: '2px', fontSize: '8px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>DRAFT</div>
            </div>
          </div>
        )}

        <div style={{ borderTop: '0.5px solid #e2e8f0', margin: '6px 0' }} />

        <div className="flex justify-between mb-3">
          <div>
            <div style={{ fontSize: '7px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 600, marginBottom: '2px' }}>Billed to</div>
            <div style={{ fontWeight: 600, color: '#0f172a' }}>Smith &amp; Partners LLP</div>
            <div style={{ color: '#64748b' }}>1 Legal Square, London EC2A 4AB</div>
          </div>
          <div style={{ textAlign: 'right' }}>
            <div style={{ fontSize: '7px', letterSpacing: '0.1em', textTransform: 'uppercase', color: '#94a3b8', fontWeight: 600, marginBottom: '2px' }}>Matter</div>
            <div style={{ fontWeight: 600, color: '#0f172a' }}>MAT-2026-001</div>
            <div style={{ color: '#64748b' }}>Reynolds v. Westbridge</div>
          </div>
        </div>

        <div style={{ background: '#f1f5f9', padding: '3px 4px', display: 'flex', justifyContent: 'space-between', fontWeight: 600, color: '#0f172a', marginBottom: '1px' }}>
          <span style={{ flex: 3 }}>Description</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Hrs</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Rate</span>
          <span style={{ flex: 1, textAlign: 'right' }}>Total</span>
        </div>
        {([
          ['Brief fee', '—', '—', '£4,000.00'],
          ['Conference (1.5 hrs)', '1.50', '£650/hr', '£975.00'],
        ] as [string, string, string, string][]).map(([desc, hrs, rate, total], i) => (
          <div key={i} style={{ display: 'flex', justifyContent: 'space-between', padding: '2px 4px', borderBottom: '0.5px solid #e2e8f0', color: '#334155' }}>
            <span style={{ flex: 3 }}>{desc}</span>
            <span style={{ flex: 1, textAlign: 'right' }}>{hrs}</span>
            <span style={{ flex: 1, textAlign: 'right' }}>{rate}</span>
            <span style={{ flex: 1, textAlign: 'right', fontWeight: 600, color: '#0f172a' }}>{total}</span>
          </div>
        ))}

        <div style={{ display: 'flex', justifyContent: 'flex-end', marginTop: '6px' }}>
          <div style={{ width: '160px' }}>
            {([['Subtotal', '£4,975.00'], ['VAT (20%)', '£995.00']] as [string, string][]).map(([l, v]) => (
              <div key={l} style={{ display: 'flex', justifyContent: 'space-between', color: '#64748b', padding: '1px 0' }}>
                <span>{l}</span><span>{v}</span>
              </div>
            ))}
            <div style={{ display: 'flex', justifyContent: 'space-between', fontWeight: 700, color: '#0f172a', borderTop: '0.5px solid #94a3b8', marginTop: '3px', paddingTop: '3px', fontSize: '10px' }}>
              <span>Total</span><span>£5,970.00</span>
            </div>
          </div>
        </div>

        {footerText ? (
          <div style={{ borderTop: '0.5px solid #e2e8f0', marginTop: '8px', paddingTop: '5px', color: '#64748b', fontSize: '8px', whiteSpace: 'pre-line' }}>
            {footerText}
          </div>
        ) : (
          <div style={{ borderTop: '0.5px solid #e2e8f0', marginTop: '8px', paddingTop: '5px', color: '#cbd5e1', fontSize: '8px', fontStyle: 'italic' }}>
            Your footer text will appear here — payment terms, BSB registration, disclaimers.
          </div>
        )}

        <div style={{ textAlign: 'center', color: '#cbd5e1', fontSize: '7px', marginTop: '8px' }}>
          Generated {new Date().toLocaleDateString('en-GB')} · {firmName}
        </div>
      </div>
    </div>
  );
}

// ─── Header mode cards ────────────────────────────────────────

const HEADER_MODE_OPTIONS: { value: InvoiceHeaderMode; label: string; desc: string }[] = [
  { value: 'text',       label: 'Text Only',      desc: 'Auto-generates from your profile' },
  { value: 'logo',       label: 'Logo Header',    desc: 'Your logo with address alongside' },
  { value: 'full_image', label: 'Full Letterhead', desc: 'Complete pre-designed header image' },
];

function HeaderModeCard({ option, selected, onSelect }: {
  option: typeof HEADER_MODE_OPTIONS[0];
  selected: boolean;
  onSelect: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onSelect}
      className={'relative flex flex-col rounded-lg border-2 p-3 text-left transition-all ' + (
        selected
          ? 'border-brand-600 bg-brand-50/50 shadow-sm'
          : 'border-border bg-background hover:border-brand-300 hover:bg-muted/30'
      )}
    >
      <div className="mb-2.5 h-14 w-full overflow-hidden rounded border border-border/60 bg-white">
        {option.value === 'text' && (
          <div className="p-2">
            <div className="mb-1 h-1.5 w-20 rounded-sm bg-slate-800" />
            <div className="mb-0.5 h-1 w-14 rounded-sm bg-slate-300" />
            <div className="mb-0.5 h-1 w-12 rounded-sm bg-slate-300" />
            <div className="h-1 w-10 rounded-sm bg-slate-300" />
          </div>
        )}
        {option.value === 'logo' && (
          <div className="flex gap-2 p-2">
            <div className="h-8 w-8 flex-shrink-0 rounded bg-slate-200 flex items-center justify-center">
              <div className="h-4 w-6 rounded-sm bg-slate-400" />
            </div>
            <div className="flex-1 pt-0.5">
              <div className="mb-0.5 h-1.5 w-14 rounded-sm bg-slate-800" />
              <div className="mb-0.5 h-1 w-10 rounded-sm bg-slate-300" />
              <div className="h-1 w-8 rounded-sm bg-slate-300" />
            </div>
          </div>
        )}
        {option.value === 'full_image' && (
          <div className="h-full w-full bg-gradient-to-r from-slate-700 via-slate-600 to-slate-700 flex items-center justify-center">
            <div className="text-[8px] text-white/60 tracking-wider">YOUR LETTERHEAD</div>
          </div>
        )}
      </div>
      <div className="text-xs font-semibold text-foreground">{option.label}</div>
      <div className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{option.desc}</div>
      {selected && (
        <div className="absolute right-2 top-2">
          <CheckCircle size={14} weight="fill" className="text-brand-600 dark:text-brand-400" />
        </div>
      )}
    </button>
  );
}

// ─── Logo / image upload section ─────────────────────────────

function LogoUploadSection({ logoUrl, onLogoChange, label, hint }: {
  logoUrl: string;
  onLogoChange: (url: string) => void;
  label: string;
  hint: string;
}) {
  const [isDragging, setIsDragging] = useState(false);
  const [showUrlInput, setShowUrlInput] = useState(Boolean(logoUrl) && !logoUrl.startsWith('data:'));
  const fileRef = useRef<HTMLInputElement>(null);

  const processFile = useCallback((file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Please select a PNG, JPG or SVG image');
      return;
    }
    if (file.size > 600_000) {
      toast.error('Image must be under 600 KB');
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => {
      onLogoChange(e.target?.result as string);
      setShowUrlInput(false);
    };
    reader.readAsDataURL(file);
  }, [onLogoChange]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  }, [processFile]);

  const isDataUrl = logoUrl.startsWith('data:');
  const hasImage = Boolean(logoUrl);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <div className="text-xs font-semibold text-foreground">{label}</div>
        {hasImage && (
          <button type="button" onClick={() => onLogoChange('')}
            className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-destructive transition-colors">
            <XIcon size={10} /> Remove
          </button>
        )}
      </div>

      {hasImage ? (
        <div className="flex items-center gap-3 rounded-lg border border-border bg-muted/20 p-3">
          <div className="flex h-14 w-24 flex-shrink-0 items-center justify-center rounded border border-border bg-white p-1">
            <img src={logoUrl} alt="Preview" className="max-h-full max-w-full object-contain"
              onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-medium">{isDataUrl ? 'Uploaded file' : 'Image URL'}</div>
            <div className="truncate text-[10px] text-muted-foreground mt-0.5">
              {isDataUrl ? 'Stored as base64 — ready to use' : logoUrl}
            </div>
            <button type="button" onClick={() => fileRef.current?.click()}
              className="mt-1.5 text-[10px] text-brand-600 dark:text-brand-400 hover:underline">
              Replace image
            </button>
          </div>
        </div>
      ) : (
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={onDrop}
          onClick={() => fileRef.current?.click()}
          className={'flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed p-6 text-center transition-colors ' + (
            isDragging ? 'border-brand-500 bg-brand-50/50' : 'border-border hover:border-brand-300 hover:bg-muted/20'
          )}
        >
          <div className={'flex h-10 w-10 items-center justify-center rounded-full ' + (isDragging ? 'bg-brand-100' : 'bg-muted')}>
            <UploadSimple size={18} className={isDragging ? 'text-brand-600 dark:text-brand-400' : 'text-muted-foreground'} />
          </div>
          <div>
            <div className="text-sm font-medium">Drop image here, or <span className="text-brand-600 dark:text-brand-400">browse files</span></div>
            <div className="mt-0.5 text-[11px] text-muted-foreground">{hint}</div>
          </div>
        </div>
      )}

      <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp"
        className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) processFile(f); }} />

      <button type="button" onClick={() => setShowUrlInput((v) => !v)}
        className="flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground transition-colors">
        <ImageIcon size={11} />
        {showUrlInput ? 'Hide URL input' : 'Or paste an image URL instead'}
      </button>
      {showUrlInput && (
        <input type="url" value={isDataUrl ? '' : logoUrl} onChange={(e) => onLogoChange(e.target.value)}
          placeholder="https://your-chambers.co.uk/logo.png"
          className="w-full rounded-md border border-input bg-background px-2.5 py-1.5 text-sm" />
      )}
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────

function InvoiceAppearanceTab() {
  const qc = useQueryClient();
  const prefsQ = useQuery({ queryKey: ['chambers-prefs'], queryFn: fetchChambersPrefs });
  const [loadedFor, setLoadedFor] = useState<string | null>(null);
  const [headerMode, setHeaderMode] = useState<InvoiceHeaderMode>('text');
  const [logoUrl, setLogoUrl] = useState('');
  const [fullHeaderUrl, setFullHeaderUrl] = useState('');
  const [headerCustomText, setHeaderCustomText] = useState('');
  const [footerText, setFooterText] = useState('');

  const prefs = prefsQ.data;
  if (prefs && loadedFor !== prefs.id) {
    setLoadedFor(prefs.id);
    setHeaderMode((prefs.invoiceHeaderType as InvoiceHeaderMode) ?? 'text');
    setLogoUrl(prefs.invoiceLogoUrl ?? '');
    setFullHeaderUrl(prefs.invoiceFullHeaderUrl ?? '');
    setHeaderCustomText(prefs.invoiceHeaderCustomText ?? '');
    setFooterText(prefs.invoiceFooterText ?? '');
  }

  const saveMut = useMutation({
    mutationFn: () => updateChambersPrefs({
      invoiceHeaderType: headerMode,
      invoiceLogoUrl: logoUrl.trim() || null,
      invoiceFullHeaderUrl: fullHeaderUrl.trim() || null,
      invoiceHeaderCustomText: headerCustomText.trim() || null,
      invoiceFooterText: footerText.trim() || null,
    } as Parameters<typeof updateChambersPrefs>[0]),
    onSuccess: () => {
      toast.success('Invoice appearance saved');
      qc.invalidateQueries({ queryKey: ['chambers-prefs'] });
    },
    onError: (err: unknown) => toast.error(getApiErrorMessage(err, 'Could not save')),
  });

  if (prefsQ.isLoading) return (
    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/30 px-5 py-8 text-sm text-muted-foreground">
      <CircleNotch size={14} className="animate-spin" /> Loading branding settings…
    </div>
  );

  return (
    <div className="grid gap-6 lg:grid-cols-[1fr_360px]">

      {/* ── LEFT: Configure ── */}
      <div className="space-y-5">

        <div className="rounded-lg border border-brand-100 bg-brand-50/40 px-4 py-3">
          <div className="text-sm font-semibold text-brand-800 dark:text-brand-200">Invoice branding</div>
          <div className="mt-0.5 text-xs text-brand-700 dark:text-brand-300 leading-relaxed">
            Configure how your practice appears on every invoice sent to instructing solicitors.
            The live preview on the right updates as you make changes.
          </div>
        </div>

        {/* Header style cards */}
        <div className="space-y-3">
          <div>
            <div className="text-sm font-semibold text-foreground">Header style</div>
            <div className="mt-0.5 text-xs text-muted-foreground">How the top of your invoice is presented to clients.</div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {HEADER_MODE_OPTIONS.map((opt) => (
              <HeaderModeCard key={opt.value} option={opt} selected={headerMode === opt.value} onSelect={() => setHeaderMode(opt.value)} />
            ))}
          </div>
        </div>

        {/* Logo upload */}
        {headerMode === 'logo' && (
          <Card className="px-5 py-4">
            <LogoUploadSection
              logoUrl={logoUrl}
              onLogoChange={setLogoUrl}
              label="Practice or chambers logo"
              hint="PNG, JPG or SVG · Max 600 KB · Renders ~90px wide on invoice"
            />
          </Card>
        )}

        {/* Full letterhead upload */}
        {headerMode === 'full_image' && (
          <Card className="px-5 py-4 space-y-3">
            <div>
              <div className="text-sm font-semibold text-foreground">Letterhead image</div>
              <div className="mt-0.5 text-xs text-muted-foreground leading-relaxed">
                This image <strong>replaces the entire invoice header</strong>. Ensure it contains
                your firm name, address and contact details — nothing will be auto-added by the system.
              </div>
            </div>
            <LogoUploadSection
              logoUrl={fullHeaderUrl}
              onLogoChange={setFullHeaderUrl}
              label="Full letterhead header"
              hint="Recommended 1240 × 310 px · Max 600 KB · Renders full width on A4"
            />
          </Card>
        )}

        {/* Custom text lines */}
        {headerMode === 'text' && (
          <Card className="px-5 py-4 space-y-3">
            <div>
              <div className="text-sm font-semibold text-foreground">Additional header lines</div>
              <div className="mt-0.5 text-xs text-muted-foreground">
                Printed below your firm name. Use for call year, qualifications, BSB reference.
              </div>
            </div>
            <textarea
              value={headerCustomText}
              onChange={(e) => setHeaderCustomText(e.target.value)}
              rows={3}
              maxLength={300}
              placeholder={'James Thornton KC\nBarrister-at-Law · Called 1998\nBSB Ref: 12345'}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-500/30 resize-none"
            />
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>Each line prints on a new row</span>
              <span className={headerCustomText.length > 270 ? 'text-amber-600 dark:text-amber-400' : ''}>{headerCustomText.length}/300</span>
            </div>
          </Card>
        )}

        {/* Footer */}
        <Card className="px-5 py-4 space-y-3">
          <div>
            <div className="text-sm font-semibold text-foreground">Invoice footer</div>
            <div className="mt-0.5 text-xs text-muted-foreground">
              Appears at the bottom of every invoice. Typically payment terms, BSB registration, VAT details.
            </div>
          </div>
          <textarea
            value={footerText}
            onChange={(e) => setFooterText(e.target.value)}
            rows={3}
            maxLength={500}
            placeholder={'Payment due 30 days from date of invoice.\nRegistered with the Bar Standards Board.\nVAT Registration No. GB 123 456 789'}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm leading-relaxed focus:outline-none focus:ring-2 focus:ring-brand-500/30 resize-none"
          />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>Supports multiple lines</span>
            <span className={footerText.length > 450 ? 'text-amber-600 dark:text-amber-400' : ''}>{footerText.length}/500</span>
          </div>
        </Card>

        {/* Save bar */}
        <div className="flex items-center justify-between rounded-lg border border-border bg-muted/20 px-4 py-3">
          <div className="text-xs text-muted-foreground">
            Changes apply to all invoices generated after saving.
          </div>
          <Button onClick={() => saveMut.mutate()} disabled={saveMut.isPending} className="gap-1.5 min-w-[145px]">
            {saveMut.isPending
              ? <><CircleNotch size={14} className="animate-spin" /> Saving…</>
              : <><CheckCircle size={14} /> Save appearance</>
            }
          </Button>
        </div>

      </div>

      {/* ── RIGHT: Live preview ── */}
      <div className="space-y-2 lg:sticky lg:top-6 self-start">
        <div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Live preview</div>
        <InvoicePreview
          headerMode={headerMode}
          logoUrl={logoUrl}
          fullHeaderUrl={fullHeaderUrl}
          headerCustomText={headerCustomText}
          footerText={footerText}
          prefs={prefs}
        />
        <div className="text-center text-[10px] text-muted-foreground pt-0.5">
          Illustrative — actual PDF may differ slightly in font and spacing
        </div>
      </div>

    </div>
  );
}
